import { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import { meQuery, projectsQuery, useLogout, useCreateProject } from '../lib/auth.js';
import { getActiveProjectId, setActiveProjectId } from '../lib/api.js';
import { useTheme } from '../lib/theme.js';
import { CommandPalette } from './CommandPalette.js';

interface NavItem {
  label: string;
  to: string;
  exact?: boolean;
  expertOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Navigation entlang des Arbeitsablaufs.
 * Standardmäßig werden nur die essenziellen 6 Hauptpunkte angezeigt (Standard-Modus).
 * Über den "Experten-Modus"-Schalter im Footer werden alle fortgeschrittenen Menüpunkte freigeschaltet.
 */
const NAV: NavGroup[] = [
  {
    label: 'Akquise',
    items: [
      { label: 'Übersicht', to: '/', exact: true },
      { label: 'Kontakte', to: '/kontakte' },
      { label: 'Sperrliste', to: '/kontakte/sperrliste' },
      { label: 'Firmen', to: '/firmen', expertOnly: true },
    ],
  },
  {
    label: 'Versand',
    items: [
      { label: 'Kampagnen', to: '/kampagnen' },
      { label: 'Warteschlange', to: '/warteschlange' },
      { label: 'Segmente', to: '/segmente', expertOnly: true },
      { label: 'Journeys', to: '/journeys', expertOnly: true },
      { label: 'Zustellbarkeit', to: '/zustellbarkeit', expertOnly: true },
    ],
  },
  {
    label: 'Nachverfolgung',
    items: [
      { label: 'Posteingang', to: '/inbox' },
      { label: 'Auswertung', to: '/auswertung', expertOnly: true },
    ],
  },
  {
    label: 'Vertrieb',
    items: [
      { label: 'Action Stream', to: '/crm/action-stream' },
      { label: 'Verkaufstafel', to: '/crm', exact: true },
      { label: 'Rezensionsfälle', to: '/crm/review-cases' },
      { label: 'Aufgaben', to: '/crm/aufgaben', expertOnly: true },
    ],
  },
];

export function AppShell() {
  const { data: user } = useQuery(meQuery);
  const { data: projectsData } = useQuery(projectsQuery);
  const logout = useLogout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { theme, toggle } = useTheme();

  const [menuOffen, setMenuOffen] = useState(false);

  useEffect(() => {
    if (!menuOffen) return;
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const beiEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOffen(false);
    };
    document.addEventListener('keydown', beiEsc);

    return () => {
      document.body.style.overflow = vorher;
      document.removeEventListener('keydown', beiEsc);
    };
  }, [menuOffen]);

  const [expertMode, setExpertMode] = useState<boolean>(() => {
    try {
      return localStorage.getItem('mailflow_expert_mode') === 'true';
    } catch {
      return false;
    }
  });

  const toggleExpertMode = () => {
    setExpertMode((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('mailflow_expert_mode', String(next));
      } catch {}
      return next;
    });
  };

  const projects = projectsData?.items ?? [];
  const activeProjectId = getActiveProjectId() ?? projects[0]?.id ?? '';

  async function handleLogout() {
    await logout.mutateAsync().catch(() => undefined);
    await navigate({ to: '/login' });
  }

  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjName, setNewProjName] = useState('');
  const [newProjDomain, setNewProjDomain] = useState('');
  const createProject = useCreateProject();

  const handleProjectSwitch = (id: string) => {
    if (id === '__new__') {
      setShowNewProjectModal(true);
      return;
    }
    setActiveProjectId(id);
    void queryClient.invalidateQueries();
    window.location.reload();
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProjName.trim()) return;
    try {
      const res = await createProject.mutateAsync({
        name: newProjName.trim(),
        domain: newProjDomain.trim() || undefined,
      });
      setShowNewProjectModal(false);
      setNewProjName('');
      setNewProjDomain('');
      setActiveProjectId(res.project.id);
      void queryClient.invalidateQueries();
      window.location.reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Projekt konnte nicht angelegt werden.');
    }
  };

  // Menü-Filterung nach Experten-Modus
  const visibleNav = NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => expertMode || !item.expertOnly),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="app-layout" data-menu={menuOffen ? 'open' : undefined}>
      <CommandPalette />

      <a href="#hauptinhalt" className="skip-link">
        Zum Hauptinhalt springen
      </a>

      {/* Nur auf schmalem Schirm sichtbar (siehe mobile.css). */}
      <header className="topbar">
        <button
          type="button"
          className="topbar__menu"
          aria-expanded={menuOffen}
          aria-controls="hauptnavigation"
          onClick={() => setMenuOffen((offen) => !offen)}
        >
          <span aria-hidden="true">{menuOffen ? '✕' : '☰'}</span>
          <span className="sr-only">{menuOffen ? 'Menü schließen' : 'Menü öffnen'}</span>
        </button>

        <Link to="/" className="topbar__brand">
          MailFlow
        </Link>

        <button
          type="button"
          className="topbar__theme"
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Zu hellem Design wechseln' : 'Zu dunklem Design wechseln'}
        >
          <span aria-hidden="true">{theme === 'dark' ? '🌙' : '☀️'}</span>
        </button>
      </header>

      {/* Der Vorhang liegt zwischen Schublade und Inhalt; ein Klick schliesst. */}
      <div
        className="sidebar__scrim"
        onClick={() => setMenuOffen(false)}
        aria-hidden="true"
        hidden={!menuOffen}
      />

      <aside className="sidebar" id="hauptnavigation">
        <div className="sidebar__brand">
          <Link to="/" className="sidebar__logo">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M22 13V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              <path d="M19 16v6" />
              <path d="M16 19h6" />
            </svg>
            <span>MailFlow 2.0</span>
          </Link>
        </div>

        <div className="sidebar__project">
          <label className="sidebar__label" htmlFor="project-select">
            Projekt
          </label>
          <select
            id="project-select"
            className="select select--sm"
            value={activeProjectId}
            onChange={(e) => handleProjectSwitch(e.target.value)}
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            <option value="__new__">+ Neues Projekt anlegen …</option>
          </select>
        </div>

        {showNewProjectModal && (
          <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && setShowNewProjectModal(false)}>
            <div className="card dialog" role="dialog" aria-modal="true" style={{ maxWidth: '440px' }}>
              <div className="card__body stack" style={{ gap: 'var(--space-4)' }}>
                <h2 style={{ fontSize: 'var(--text-lg)' }}>Neues Projekt anlegen</h2>
                <form onSubmit={handleCreateProject} className="stack" style={{ gap: 'var(--space-3)' }}>
                  <Field
                    label="Projektname"
                    type="text"
                    value={newProjName}
                    onChange={(e) => setNewProjName(e.target.value)}
                    placeholder="z. B. Rundgänge 360"
                    required
                    autoFocus
                  />
                  <Field
                    label="Projektdomain (optional)"
                    type="text"
                    value={newProjDomain}
                    onChange={(e) => setNewProjDomain(e.target.value)}
                    placeholder="z. B. swiss-media360.ch"
                  />
                  <div className="cluster" style={{ justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                    <Button type="button" variant="ghost" onClick={() => setShowNewProjectModal(false)}>
                      Abbrechen
                    </Button>
                    <Button type="submit" loading={createProject.isPending}>
                      Projekt anlegen & wechseln
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        <nav className="sidebar__nav" aria-label="Hauptnavigation" onClick={() => setMenuOffen(false)}>
          {visibleNav.map((group) => (
            <div key={group.label} className="sidebar__group">
              <div className="sidebar__group-label">{group.label}</div>
              {group.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  activeProps={{ className: 'sidebar__link is-active' }}
                  inactiveProps={{ className: 'sidebar__link' }}
                  activeOptions={{ exact: item.exact }}
                >
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__footer">
          <button type="button" className="sidebar__link sidebar__theme" onClick={toggle}>
            <span>Design: {theme === 'dark' ? 'Dunkel' : 'Hell'}</span>
          </button>

          <button type="button" className="sidebar__link sidebar__theme" onClick={toggleExpertMode}>
            <span>Expertenmodus: {expertMode ? 'An' : 'Aus'}</span>
          </button>

          {user && (
            <div className="sidebar__user">
              <span className="sidebar__user-email">{user.email}</span>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                onClick={() => void handleLogout()}
              >
                Abmelden
              </button>
            </div>
          )}
        </div>
      </aside>

      <main className="main-content" id="hauptinhalt">
        <Outlet />
      </main>
    </div>
  );
}
